import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ZFields } from '@common/data-models/fields/z-fields';
import { FilterDef } from '@common/data-models/navigation/filter-def';
import { ViewStateFilter } from '@common/data-models/navigation/viewstate-filter';
import { SearchViewStateRecord } from '@common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@common/data-models/records/single-viewstate-record';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { ViewStateTypes, ViewStateTypesEnum } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { ViewStateRecordsAlias } from '@common/services/layout-state';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Util } from '@common/utils/util';
import { NOMDatabase } from '@src/suite/common/store/records/records.models';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { filterFilterByObjectTypes } from '@src/suite/common/utils/viewstate-utils';
import { ViewStateOptions } from '@suite/common/data-models/navigation/viewstate-options';

@Injectable({ providedIn: 'root' })
export class PlaybookSearchService {
    private nomWebServicesJson = inject(NomWebServicesJson);
    private globalTablesService = inject(GlobalTablesService);
    private visualStateService = inject(VisualStateService);

    public checkForTypePrivs(objectType: ObjectTypes): Array<number> {
        const typesArray = [];
        if (objectType === ObjectTypes.TOPIC || objectType === ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.TOPIC);
        }
        if (objectType === ObjectTypes.ASSIGNMENT || objectType === ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.ASSIGNMENT);
        }
        if (objectType === ObjectTypes.SCRIPT || objectType === ObjectTypes.UNASSIGNED) {
            typesArray.push(ObjectTypes.SCRIPT);
        }
        if (objectType === ObjectTypes.RUNDOWN) {
            typesArray.push(ObjectTypes.RUNDOWN);
        }
        return typesArray;
    }

    public async buildTemplateServiceParams(
        filter: ViewStateFilter,
        applyPrivs: boolean,
        sortBy: any,
        objectType?: number,
        search?: string,
        poller?: boolean,
        groupTemplatesLocation?: string,
        templateFolder: string = 'L'
    ): Promise<any> {
        filter = filter ? filter.deepCopy() : new ViewStateFilter();

        const zFields = new ZFields(true);
        filter.groups = [];
        if (groupTemplatesLocation) {
            filter.groups.push(FilterDef.create('virtualPath', 'TEXT', `\\\\${this.nomWebServicesJson.PSTServer}\\\\P_${groupTemplatesLocation}\\\\${templateFolder}`));
        }
        // get enterprise templates
        filter.groups.push(FilterDef.create('virtualPath', 'TEXT', `\\\\${this.nomWebServicesJson.PSTServer}\\\\P_${this.nomWebServicesJson.PSTGroup}\\\\${templateFolder}`));
        // get personal templates
        filter.groups.push(FilterDef.create('virtualPath', 'TEXT', `\\\\${this.nomWebServicesJson.PSTServer}\\\\U_${this.nomWebServicesJson.UserId}\\\\${templateFolder}`));

        let objTypes = [];
        if (applyPrivs) {
            if (objectType) {
                objTypes = this.checkForTypePrivs(objectType);
                zFields.add('type', objTypes.join(';'));
            } else {
                objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                zFields.add('type__list__', objTypes.join(';'));
            }
        } else if (objectType) {
            zFields.add('type', objectType);
        }

        await this.fillZFieldsFromFilter(objTypes, zFields, filter);

        const nomLocations = [
            {
                BasePath: `${this.nomWebServicesJson.PSTServer}\\`,
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false,
            },
        ];

        return {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: '',
            zFields: zFields.data,
            NOMLocations: nomLocations,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: this.visualStateService.ConfigSettings.MaxRows,
            SortByRank: false,
            SortFields: null,
            ExactMatch: false,
        };
    }

    public async getStoryTemplateSearchResults(vs: ViewStateRecordsAlias, groupTemplatesPath?: string): Promise<ServiceResponse<StorytellingStoryRecord[]>> {
        const results: StorytellingStoryRecord[] = [];
        let response: HttpResponse<any> = null;

        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;
        const storyRequestParams = await this.buildTemplateServiceParams(
            null,
            true,
            singleViewState.viewStateSorting,
            ObjectTypes.SCRIPT,
            searchViewState.viewStateSearchQuery,
            null,
            groupTemplatesPath,
            'X'
        );

        storyRequestParams.IncludeChildren = true;
        storyRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', storyRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
            });
            return new ServiceResponse<StorytellingStoryRecord[]>(true, results);
        }

        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                if (p.ListData.Type === ObjectTypes.SCRIPT && p.ListData.Path.includes(`${groupTemplatesPath}\\X`)) {
                    // 3. story results
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                    const storyTemplateRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(serviceRecord);
                    results.push(storyTemplateRecord);
                }
            }
        }
        return new ServiceResponse<StorytellingStoryRecord[]>(true, results);
    }

    public async getTemplateSearchResults(vs: ViewStateRecordsAlias, groupTemplatesPath?: string): Promise<ServiceResponse<StorytellingTopicRecord[]>> {
        const results: StorytellingTopicRecord[] = [];
        let response: HttpResponse<any> = null;

        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;
        const topicRequestParams = await this.buildTemplateServiceParams(
            null,
            true,
            singleViewState.viewStateSorting,
            ObjectTypes.TOPIC,
            searchViewState.viewStateSearchQuery,
            null,
            groupTemplatesPath
        );
        topicRequestParams.IncludeChildren = true;
        topicRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', topicRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
            });
            return new ServiceResponse<StorytellingTopicRecord[]>(true, results);
        }

        // parentGuid to guid map
        const _assignmentParentGuids: { [id: string]: Array<string> } = {};
        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                if (p.ListData.Type === 137) {
                    // 1. add Topic results
                    const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    const topicTemplateRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceCollectionRecord);

                    const record = topicTemplateRecord;
                    // record.setChildren([]);
                    // add the templates
                    // TODO: add the templates to the store
                    results.push(record);
                    // for (const childScript of p.CollectionScripts) {
                    //     const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecord<AssignmentRecord>(childScript);
                    //     const assignment = assignmentRecord;
                    //     if (record && !record.hasChildWithGuid(assignment.guid)) {
                    //         record.unshiftChildren(assignment);
                    //     }
                    // }
                }
                if (p.ListData.Type === 138) {
                    // 2. assignment results - that may or may not have topics
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                    const _assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(serviceRecord);
                    // results.addAssignment(assignmentRecord);
                    // if (!assignmentParentGuids[assignmentRecord.parentGuid]) assignmentParentGuids[assignmentRecord.parentGuid] = [];
                    // assignmentParentGuids[assignmentRecord.parentGuid].push(assignmentRecord.guid);
                }

                if (p.ListData.Type === ObjectTypes.SCRIPT) {
                    // 3. add Story results
                    const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    const _storyRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingStoryRecord>(serviceCollectionRecord);
                }
            }
        }
        return new ServiceResponse<StorytellingTopicRecord[]>(true, results);
    }

    protected async buildFilterServiceParams(
        filter: ViewStateFilter,
        applyPrivs: boolean,
        sortBy: any,
        sortOrder: string,
        objectType?: number,
        search?: string,
        poller?: boolean,
        guid?: string,
        specificObjectTypes?: Array<ObjectTypes>,
        database?: NOMDatabase
    ): Promise<any> {
        let QueryTerms: string = '';
        if (search) {
            QueryTerms = search;
        }

        filter = filter ? filter.deepCopy() : new ViewStateFilter();
        const virtualPaths: Array<string> = [];

        const zFields = new ZFields(true);

        if (database) {
            for (const location of database.NOMLocations) {
                virtualPaths.push(`\\\\${database.Database}\\\\${location.BasePath}\\\\W`);
            }
        } else {
            virtualPaths.push(`\\\\${this.nomWebServicesJson.PSTServer}\\\\P_${this.nomWebServicesJson.PSTGroup}\\\\W`);
        }
        zFields.add('virtualPath__list__', virtualPaths.join(';'));

        const normalizedObjectType = (specificObjectTypes || []).filter(o => o !== ObjectTypes.UNASSIGNED);
        let objTypes = [];
        if (applyPrivs || normalizedObjectType?.length > 0) {
            if (normalizedObjectType?.length > 0) {
                objTypes = normalizedObjectType;
                if (normalizedObjectType.length === 1) {
                    zFields.add('type', normalizedObjectType[0]);
                } else {
                    zFields.add('type__list__', normalizedObjectType.join(';'));
                }
            } else {
                if (guid) {
                    objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                    zFields.add('type__list__', objTypes.join(';'));
                } else if (objectType) {
                    objTypes = this.checkForTypePrivs(objectType);
                    zFields.add('type', objTypes.join(';'));
                } else {
                    objTypes = this.checkForTypePrivs(ObjectTypes.UNASSIGNED);
                    zFields.add('type__list__', objTypes.join(';'));
                }
            }
        } else if (objectType) {
            zFields.add('type', objectType);
        }

        const queryTermTags: Array<string> = [];
        const queryTermUsers: Array<string> = [];
        const queryTermWords: Array<string> = [];

        let queryTermsArr = [];

        if (QueryTerms.startsWith('#')) {
            queryTermTags.push(QueryTerms.substr(1));
        } else {
            // we should support search for tags which consists of more than one word
            queryTermsArr = QueryTerms.split(' ');
        }
        for (const term of queryTermsArr) {
            if (term.startsWith('@')) {
                queryTermUsers.push(term.substr(1));
            } else {
                queryTermWords.push(term);
            }
        }

        if (queryTermTags.length > 0) {
            const values: Array<string> = [];
            if (filter.map[PSTFieldNames.PSTTags] && filter.map[PSTFieldNames.PSTTags].Value && Array.isArray(filter.map[PSTFieldNames.PSTTags].Value)) {
                for (const v of <Array<string>>filter.map[PSTFieldNames.PSTTags].Value) {
                    if (values.indexOf(v) < 0) {
                        values.push(v);
                    }
                }
            }
            if (queryTermTags.length > 0) {
                for (const termTag of queryTermTags) {
                    if (values.indexOf(termTag) < 0) {
                        values.push(termTag);
                    }
                }
            }
            if (values.length > 0) {
                filter.push(FilterDef.create(PSTFieldNames.PSTTags, 'LOOKUPLIST', values));
            }
        }

        if (queryTermUsers.length > 0) {
            for (const field of this.globalTablesService.FieldDefs) {
                const isStaffIDList = this.globalTablesService.isStaffIDListFieldType(field.ID);
                if (isStaffIDList) {
                    if (queryTermUsers.length > 0) {
                        for (const termUser of queryTermUsers) {
                            filter.groups.push(FilterDef.create(field.ID, 'STAFFIDLIST', termUser));
                        }
                    }
                }
            }
        }
        await this.fillZFieldsFromFilter(objTypes, zFields, filter, guid);
        let sortFields = [];
        if (!guid) {
            sortFields = this.buildSortFields(sortBy, sortOrder);
        }

        return {
            Database: database?.Database || this.nomWebServicesJson.PSTServer,
            QueryTerms: queryTermWords.length === 0 ? '' : queryTermWords.join(' '),
            zFields: zFields.data,
            NOMLocations: database?.NOMLocations || [
                {
                    BasePath: `P_${this.nomWebServicesJson.PSTGroup}\\`,
                    SearchWIP: true,
                    SearchArchives: true,
                    SearchTrash: false,
                },
            ],
            NOMContentTypes: {
                All: objectType === ObjectTypes.RUNDOWN ? false : true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: objectType === ObjectTypes.RUNDOWN ? true : false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            // MaxRows: maxResults,
            MaxRows: this.visualStateService.ConfigSettings.MaxRows,
            SortByRank: false,
            SortFields: sortFields,
            ExactMatch: false,
        };
    }

    public async getFilteredResultsGuids(vs: ViewStateRecordsAlias): Promise<ServiceResponse<Array<{ [id: string]: any }>>> {
        const ret: Array<{ [id: string]: any }> = [];

        const objectType = this.getObjectTypeByViewStateType(vs.viewStateType);
        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;
        const requestParams = await this.buildFilterServiceParams(singleViewState.viewStateFilter, true, { Value: 'CreatedDateTime' }, 'desc', objectType, searchViewState.viewStateSearchQuery, false);

        requestParams.FieldsToReturn = ['guid', 'type', PSTFieldNames.PSTDesk, 'CreatedDateTime'];
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/SearchFields', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            Util.saveWarningError(`Failed to load filtered results! StackTrace: NomWebServicesJson.getFilteredResultsGuids(); Error:${error.toString()}`);
            return new ServiceResponse<Array<{ [id: string]: any }>>(false, ret);
        }

        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                const obj: { [id: string]: any } = {};
                for (const item of p) {
                    obj[item.FieldName] = item.FieldValue;
                }
                ret.push(obj);
            }
            return new ServiceResponse<Array<{ [id: string]: any }>>(true, ret);
        }
        return new ServiceResponse<Array<{ [id: string]: any }>>(false, 'Failed to load updated guid list from web service');
    }

    private getObjectTypeByViewStateType(vsType: ViewStateTypesEnum, guid?: string) {
        if (!vsType && guid) {
            return ObjectTypes.UNASSIGNED;
        }
        if (vsType === ViewStateTypes.SCRIPT) {
            return ObjectTypes.SCRIPT;
        }
        if (vsType === ViewStateTypes.ASSIGNMENT) {
            return ObjectTypes.ASSIGNMENT;
        }
        if (guid) {
            return ObjectTypes.UNASSIGNED;
        }
        return null;
    }

    public async getPlaybookResults(vs: ViewStateRecordsAlias, guid?: string, database?: NOMDatabase): Promise<ServiceResponse<PlaybookSearchResult>> {
        let response: HttpResponse<any> = null;

        const objectType = this.getObjectTypeByViewStateType(vs.viewStateType, guid);
        const singleViewState = <SingleViewStateRecord>vs;
        const searchViewState = <SearchViewStateRecord>vs;

        const topicRequestParams = await this.buildFilterServiceParams(
            singleViewState.viewStateFilter,
            true,
            singleViewState.viewStateSorting,
            singleViewState.viewStateSortOrder,
            objectType,
            searchViewState.viewStateSearchQuery,
            false,
            guid,
            searchViewState.recordTypes || null,
            database
        );
        topicRequestParams.IncludeChildren = true;
        topicRequestParams.IncludeStories = true;
        topicRequestParams.IncludeLinkedRecords = true;
        topicRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', topicRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
            });
            return new ServiceResponse<PlaybookSearchResult>(false, {
                topics: new Map<string, StorytellingTopicRecord>(),
                assignments: new Map<string, StorytellingAssignmentRecord>(),
                stories: new Map<string, StorytellingStoryRecord>(),
                discoverItems: new Map<string, StorytellingRecord>(),
                rundowns: new Map<string, StorytellingRundownRecord>(),
                listGuids: new Set<string>(),
            });
        }

        let results = null;
        if (response) {
            const respObj = response.body;
            results = this.processSearchResults(respObj.SearchResults, objectType);
        }
        return new ServiceResponse<PlaybookSearchResult>(true, results);
    }

    public async getRundownResults(vs: ViewStateRecordsAlias, database?: NOMDatabase): Promise<ServiceResponse<PlaybookSearchResult>> {
        let response: HttpResponse<any> = null;

        const objectType = ObjectTypes.RUNDOWN;
        const searchViewState = <SearchViewStateRecord>vs;

        const requestParams = await this.buildFilterServiceParams(
            searchViewState.viewStateFilter,
            true,
            searchViewState.viewStateSorting,
            searchViewState.viewStateSortOrder,
            objectType,
            searchViewState.viewStateSearchQuery,
            false,
            null,
            null,
            database
        );
        requestParams.IncludeChildren = false;
        requestParams.IncludeStories = false;
        requestParams.IncludeLinkedRecords = false;
        requestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', requestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
            });
            return new ServiceResponse<PlaybookSearchResult>(false, {
                topics: new Map<string, StorytellingTopicRecord>(),
                assignments: new Map<string, StorytellingAssignmentRecord>(),
                stories: new Map<string, StorytellingStoryRecord>(),
                discoverItems: new Map<string, StorytellingRecord>(),
                rundowns: new Map<string, StorytellingRundownRecord>(),
                listGuids: new Set<string>(),
            });
        }

        let results = null;
        if (response) {
            const respObj = response.body;
            results = this.processSearchResults(respObj.SearchResults, objectType);
        }
        return new ServiceResponse<PlaybookSearchResult>(true, results);
    }

    private processSearchResults(searchResults: Array<any>, objectType?: ObjectTypes): PlaybookSearchResult {
        const topicResults = new Map<string, StorytellingTopicRecord>();
        const assignmentResults = new Map<string, StorytellingAssignmentRecord>();
        const storiesResults = new Map<string, StorytellingStoryRecord>();
        const discoverItemsResults = new Map<string, StorytellingRecord>();
        const rundownsResults = new Map<string, StorytellingRundownRecord>();
        const listGuids = new Set<string>();
        for (const p of searchResults) {
            if (p.ListData.Type === ObjectTypes.TOPIC) {
                // 1. add Topic results
                const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                const topicRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceCollectionRecord);
                topicResults.set(topicRecord.Guid, topicRecord);
                for (const childScript of p.CollectionScripts) {
                    const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(childScript);
                    assignmentResults.set(assignmentRecord.Guid, assignmentRecord);
                }

                if (p?.Stories?.length > 0) {
                    for (const story of p.Stories) {
                        const serviceStoryRecord: ServiceRecord = ServiceRecord.instance(story);
                        const storyRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(serviceStoryRecord);
                        if (storyRecord && !storiesResults.has(storyRecord.Guid)) {
                            // Don't overwrite existing stories with partial records
                            storiesResults.set(storyRecord.Guid, storyRecord);
                        }
                    }
                }
                if (p?.LinkedRecords?.length > 0) {
                    for (const linkedRecord of p.LinkedRecords) {
                        const serviceLinkedRecord: ServiceRecord = ServiceRecord.instance(linkedRecord);
                        const linkedRecordItem = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingRecord>(serviceLinkedRecord);
                        switch (linkedRecordItem?.Type) {
                            case ObjectTypes.ASSIGNMENT:
                                assignmentResults.set(linkedRecordItem.Guid, linkedRecordItem);
                                break;
                            case ObjectTypes.TOPIC:
                                topicResults.set(linkedRecordItem.Guid, linkedRecordItem);
                                break;
                            case ObjectTypes.SCRIPT:
                                storiesResults.set(linkedRecordItem.Guid, linkedRecordItem);
                                break;
                            case ObjectTypes.DISCOVER_ITEM:
                                discoverItemsResults.set(linkedRecordItem.Guid, linkedRecordItem);
                                break;
                        }
                    }
                }

                if (!objectType || objectType === ObjectTypes.TOPIC) {
                    listGuids.add(topicRecord.Guid);
                }
            }
            if (p.ListData.Type === ObjectTypes.ASSIGNMENT) {
                // 2. assignment results - that may or may not have topics
                const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(serviceRecord);
                const parentRec = initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC);
                parentRec.Guid = assignmentRecord.ParentGuid;
                parentRec.PSTCategory = assignmentRecord.ParentCategory;
                parentRec.PSTTopicType = assignmentRecord.ParentType;
                parentRec.PSTTopicOwner = assignmentRecord.ParentOwner;
                parentRec.PSTTopicAddress = assignmentRecord.ParentLocation;
                parentRec.Title = assignmentRecord.ContainerTitle;
                parentRec.Database = assignmentRecord.Database;
                parentRec.Path = assignmentRecord.Path.parentCollectionPath;
                if (!topicResults.has(parentRec.Guid)) {
                    topicResults.set(parentRec.Guid, parentRec);
                }
                assignmentResults.set(assignmentRecord.Guid, assignmentRecord);

                if (!objectType || objectType === ObjectTypes.ASSIGNMENT) {
                    listGuids.add(assignmentRecord.Guid);
                }
            }
            if (p.ListData.Type === ObjectTypes.SCRIPT && !p.ListData.Path?.includes('S_ARCHIVE')) {
                // 3 Story results
                const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                const storyRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingStoryRecord>(serviceRecord);
                const parentRec = initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC);
                parentRec.Guid = storyRecord.PSTStoryTopicGUID;
                parentRec.PSTCategory = storyRecord.ParentCategory;
                parentRec.PSTTopicType = storyRecord.ParentType;
                parentRec.PSTTopicOwner = storyRecord.ParentOwner;
                parentRec.PSTTopicAddress = storyRecord.ParentLocation;
                parentRec.Title = storyRecord.PSTStoryTopicTitle;
                parentRec.Database = storyRecord.Database;
                parentRec.Path = storyRecord.Path.parentCollectionPath;
                if (!topicResults.has(parentRec.Guid)) {
                    topicResults.set(parentRec.Guid, parentRec);
                }
                storiesResults.set(storyRecord.Guid, storyRecord);
                if (!objectType || objectType === ObjectTypes.SCRIPT) {
                    listGuids.add(storyRecord.Guid);
                }
            }
            if (p.ListData.Type === ObjectTypes.RUNDOWN) {
                console.log('Todo: process rundown ...');
            }
        }
        return {
            topics: topicResults,
            assignments: assignmentResults,
            stories: storiesResults,
            discoverItems: discoverItemsResults,
            rundowns: rundownsResults,
            listGuids,
        };
    }

    protected buildSortFields(sortByObj: ViewStateOptions, sortOrder: string): Array<{ [id: string]: string }> {
        let sortBy = sortByObj.Value;
        if (sortBy) {
            // strip quotes before applying to search query
            sortBy = sortBy.replace(/"/g, '');
            // replace -date -time parts
            sortBy = sortBy.replace(/(-.*)/g, '');
        }
        if (sortBy === PSTFieldNames.PSTModTime) sortBy = 'modTime';
        const sortFields = [];
        if (sortBy && sortOrder) {
            sortFields.push({ FieldName: sortBy, FieldValue: sortOrder });
        }
        sortFields.push({ FieldName: 'guid', FieldValue: 'asc' });
        return sortFields;
    }

    public async getNewItems(guids: Array<string>): Promise<ServiceResponse<PlaybookSearchResult>> {
        let response: HttpResponse<any> = null;

        const topicRequestParams: any = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: '',
            zFields: [{ FieldName: 'guid__list__', FieldValue: guids.join(';') }],
            NOMLocations: [
                {
                    BasePath: `P_${this.nomWebServicesJson.PSTGroup}\\`,
                    SearchWIP: true,
                    SearchArchives: true,
                    SearchTrash: false,
                },
            ],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: 200,
            SortByRank: false,
            SortFields: {},
            ExactMatch: false,
        };
        topicRequestParams.IncludeChildren = true;
        topicRequestParams.IncludeStories = true;
        topicRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];

        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', topicRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load new items!',
                detailMessage: `Failed to load new items! \n\rStackTrace: NomWebServicesJson.getNewItems(); \n\rError: ${error}`,
            });
            return new ServiceResponse<PlaybookSearchResult>(false, {
                topics: new Map<string, StorytellingTopicRecord>(),
                assignments: new Map<string, StorytellingAssignmentRecord>(),
                stories: new Map<string, StorytellingStoryRecord>(),
                discoverItems: new Map<string, StorytellingRecord>(),
                rundowns: new Map<string, StorytellingRundownRecord>(),
                listGuids: new Set<string>(),
            });
        }
        let results = null;
        if (response) {
            const respObj = response.body;
            results = this.processSearchResults(respObj.SearchResults);
        }

        return new ServiceResponse<PlaybookSearchResult>(true, results);
    }

    private resolveFieldName(name: string): string {
        if (!name || typeof name !== 'string') {
            return name;
        }
        // if ModTime is in the field name lower the first letter
        return name.startsWith('ModTime') ? name.charAt(0).toLowerCase() + name.slice(1) : name;
    }

    private async getOwnersFilter(filters: FilterDef[]): Promise<Array<string>> {
        const pstLocationFilter = filters.filter(f => f.ID === 'PSTTeamLocation')[0];
        const pstTopicOwnerFilter = filters.filter(f => f.ID === 'PSTTopicOwner')[0];
        let filteredLocations = [];
        let filteredOwners = [];
        if (pstLocationFilter && !pstLocationFilter.NotUsed && !pstLocationFilter.IsAllSelected) {
            filteredLocations = pstLocationFilter.Value;
            if (pstTopicOwnerFilter && !pstTopicOwnerFilter?.NotUsed && Object.keys(pstTopicOwnerFilter?.Value?.data)?.length > 0) {
                filteredOwners = Object.keys(this.globalTablesService.Staff).filter(
                    s => filteredLocations.indexOf(this.globalTablesService.Staff[s].Program) > -1 && pstTopicOwnerFilter.Value.data[this.globalTablesService.Staff[s].UserID]
                );
                if (filteredOwners.length === 0) {
                    filteredOwners.push('@no_owners');
                }
            } else {
                filteredOwners = Object.keys(this.globalTablesService.Staff).filter(s => filteredLocations.indexOf(this.globalTablesService.Staff[s].Program) > -1);
                if (pstTopicOwnerFilter) {
                    pstTopicOwnerFilter.NotUsed = false;
                }
            }
            pstLocationFilter.NotUsed = true;
        }
        return filteredOwners;
    }

    protected async fillZFieldsFromFilter(objTypes: Array<number>, zFields: ZFields, filterObj: ViewStateFilter, guid?: string) {
        const filters = filterFilterByObjectTypes(filterObj, objTypes, this.globalTablesService);

        if (!guid) {
            for (const group of filters.groups) {
                const value = group.group();
                if (value) {
                    for (const g of value) {
                        zFields.add(this.resolveFieldName(g.FieldName), g.FieldValue);
                    }
                }
            }

            const filteredOwners = await this.getOwnersFilter(filters.filters);

            for (const filter of [...filters.filters, ...filters.relationships]) {
                if (filter.ID === PSTFieldNames.PSTServerLocations) continue;
                if (filter.NotUsed) {
                    // neither in the required nor in the optional list
                    continue;
                }
                const value = filter.get(filteredOwners);
                if (value) {
                    let fieldName = value.FieldName;
                    if (filter.ID === PSTFieldNames.PSTROModTime) {
                        fieldName = fieldName.replace('RO', '');
                    }
                    zFields.add(this.resolveFieldName(fieldName), value.FieldValue);
                }
            }
        }
        if (guid) {
            zFields.add('guid', guid);
        }
        return zFields;
    }
}
